/**
 * Adjutant collective-assist — rescue seed bias (Claim B chase banned).
 * FEAR: personal packChew AA outranks rescue. Legible cooldown / geometry variants.
 */
import { judgmentNorm } from "./traits";
import type { CoverSubject } from "./cover-subject";
import type { RaidUnit } from "../raid-types";
import { getJudgmentAssistKnobs, rescueEnabled } from "./knobs";
import { getMatrixModules } from "./matrix-policies";

export type RescueBiasArgs = {
  healer: Pick<RaidUnit, "id" | "x" | "y" | "attackRange" | "classId">;
  subject: CoverSubject | null;
  bond: number;
  seed: { x: number; y: number };
  subjectOutOfStaff: boolean;
  /** Absolute sim time — cooldown / hysteresis. */
  time?: number;
  /** FEAR: true when healer feet in packChew AA (caller supplies Soft≪AA). */
  healerInPackChewAa?: boolean;
  /** Optional tank for tank_screen geometry. */
  tank?: Pick<RaidUnit, "x" | "y"> | null;
};

export type RescueBiasResult = {
  x: number;
  y: number;
  pull01: number;
  active: boolean;
};

/** Per-healer last rescue activation time. */
const rescueLastAt = new Map<number, number>();

export function clearRescueCooldownState(): void {
  rescueLastAt.clear();
}

/**
 * Soft-pull adjutant/kit seed toward cover-subject intercept.
 */
export function biasAdjutantSeedForRescue(
  args: RescueBiasArgs,
): RescueBiasResult {
  const {
    healer,
    subject,
    bond,
    seed,
    subjectOutOfStaff,
    time = 0,
    healerInPackChewAa = false,
    tank = null,
  } = args;
  if (!rescueEnabled()) {
    return { x: seed.x, y: seed.y, pull01: 0, active: false };
  }
  const mods = getMatrixModules();
  // F.E.A.R.: personal survival > squad assist.
  if (mods.fearSelfInAaBlock && healerInPackChewAa) {
    return { x: seed.x, y: seed.y, pull01: 0, active: false };
  }
  const k = getJudgmentAssistKnobs();
  const minCrit = mods.daoStrictCrit
    ? Math.max(k.rescueMinCrit01, 0.8)
    : k.rescueMinCrit01;
  if (
    healer.classId !== "healer" ||
    !subject ||
    !subjectOutOfStaff ||
    subject.crit01 < minCrit ||
    bond < k.rescueBondMin ||
    k.rescuePullMax <= 0
  ) {
    return { x: seed.x, y: seed.y, pull01: 0, active: false };
  }
  if (mods.rescueCooldownSec > 0) {
    const last = rescueLastAt.get(healer.id) ?? -1e9;
    if (time - last < mods.rescueCooldownSec) {
      return { x: seed.x, y: seed.y, pull01: 0, active: false };
    }
  }
  const bondT = judgmentNorm(bond);
  const raw = Math.max(0, 0.04 + 0.2 * Math.max(0, bondT));
  const pull01 = Math.min(k.rescuePullMax, raw);
  if (pull01 < 0.04) {
    return { x: seed.x, y: seed.y, pull01: 0, active: false };
  }

  let ix: number;
  let iy: number;
  if (mods.rescueGeometry === "tank_screen" && tank) {
    // Screen between tank and dying ally — not on top of ally (no Claim B chase).
    ix = tank.x * 0.45 + subject.x * 0.55;
    iy = tank.y * 0.45 + subject.y * 0.55;
  } else {
    const dx = seed.x - subject.x;
    const dy = seed.y - subject.y;
    const len = Math.hypot(dx, dy) || 1;
    const sit = Math.max(12, healer.attackRange * 0.9);
    ix = subject.x + (dx / len) * sit;
    iy = subject.y + (dy / len) * sit;
  }

  if (mods.rescueCooldownSec > 0) {
    rescueLastAt.set(healer.id, time);
  }
  return {
    x: seed.x + (ix - seed.x) * pull01,
    y: seed.y + (iy - seed.y) * pull01,
    pull01,
    active: true,
  };
}

export function assistFollowScale01(insight: number, bond: number): number {
  const insightT = judgmentNorm(insight);
  const bondT = judgmentNorm(bond);
  const localTrust = Math.max(0, Math.min(1, 0.5 + 0.5 * insightT));
  const assist = Math.max(0, Math.min(0.4, 0.12 + 0.28 * Math.max(0, bondT)));
  return assist * (1 - 0.7 * localTrust);
}
